import { FormInstance } from '@arco-design/web-react';
import { useEffect, useRef } from 'react';

interface UseFormDataSyncOptions<T> {
  /** 表单实例 */
  form: FormInstance<T>;
  /** 数据源 */
  data?: T;
  /** 触发条件，为 true 时才执行 */
  trigger?: boolean;
}
/**
 * 表单数据同步 Hook
 * 用于在数据更新时智能同步表单字段，只更新用户未修改的字段
 * @description 适用场景：用户正在编辑表单，同时后台数据可能更新
 */
export function useFormSyncEditing<T extends Record<string, any>>({
  form,
  data,
  trigger = true,
}: UseFormDataSyncOptions<T>) {
  const initialDataRef = useRef<T>({} as T);

  useEffect(() => {
    if (trigger && data) {
      const currentValues = form.getFieldsValue();
      const updatedValues: Record<string, any> = {};

      // 遍历data中每个字段，
      // 如果当前字段的值与上一次（初始时）的值相同，则说明用户未修改，可以更新为最新data中的值
      Object.keys(data).forEach((key) => {
        if (
          currentValues[key] === initialDataRef.current[key] ||
          currentValues[key] === undefined
        ) {
          updatedValues[key] = data[key];
        }
      });

      initialDataRef.current = data;
      form.setFieldsValue(updatedValues as any);
    }
  }, [form, trigger, data]);

  return {
    /** 重置初始数据引用 */
    resetInitialData: () => {
      initialDataRef.current = {} as T;
    },
    /** 获取当前初始数据 */
    getInitialData: () => initialDataRef.current,
  };
}
